import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BusinessUnitMismatchModal } from './BusinessUnitMismatchModal';
import { useBusinessUnit } from 'src/store/businessUnit/useBusinessUnit';
import {
  getBusinessUnitName,
  navigateToBusinessUnitPreferences,
} from 'src/utils/businessUnitUtils';

/**
 * BusinessUnitMismatchHandler Component
 *
 * Automatically handles business unit mismatch scenarios for shared URLs
 * Shows modal when shared URL requires different BU than user's current BU
 * Manages modal visibility and navigation logic internally
 */
export interface BusinessUnitMismatchHandlerProps {
  'isSharedMode': boolean;
  'isBUMismatch': boolean;
  'currentBusinessUnitId': string;
  'requiredBusinessUnitId'?: string;
  'returnPath': string; // Path to navigate to on dismiss (e.g., '/audits')
  'data-testid'?: string;
}

export function BusinessUnitMismatchHandler({
  isSharedMode,
  isBUMismatch,
  currentBusinessUnitId,
  requiredBusinessUnitId,
  returnPath,
  'data-testid': dataTestId,
}: BusinessUnitMismatchHandlerProps) {
  const navigate = useNavigate();
  const { businessUnitList } = useBusinessUnit();
  const [modalVisible, setModalVisible] = useState(false);

  // Show modal when BU mismatch is detected
  useEffect(() => {
    if (isBUMismatch) {
      setModalVisible(true);
    }
  }, [isBUMismatch]);

  // Don't render if no mismatch
  if (!isBUMismatch) {
    return null;
  }

  const currentBUName = getBusinessUnitName(businessUnitList, currentBusinessUnitId);
  const requiredBUName =
    isSharedMode && requiredBusinessUnitId
      ? getBusinessUnitName(businessUnitList, requiredBusinessUnitId)
      : '';

  return (
    <BusinessUnitMismatchModal
      visible={modalVisible}
      currentBusinessUnitName={currentBUName}
      requiredBusinessUnitName={requiredBUName}
      onDismiss={() => {
        setModalVisible(false);
        // Navigate away from shared URL
        navigate(returnPath);
      }}
      onSwitchBusinessUnit={() => {
        setModalVisible(false);
        if (requiredBusinessUnitId) {
          navigateToBusinessUnitPreferences(navigate, requiredBusinessUnitId);
        }
      }}
      data-testid={dataTestId}
    />
  );
}
